import { useMemo } from "react";
import { isSameMonth } from "date-fns";
import { TrendingUp, TrendingDown, Wallet, DollarSign, CreditCard, Repeat } from "lucide-react";
import { Card, CardContent } from "../client/components/ui/card";
import { CardSectionHeader, useIsDark } from "../client/components/ui/card-section-header";
import useTranslation from "../client/hooks/useTranslation";
import { CURRENCY } from "../shared/currency";
import type { CalendarEvent } from "./PropFirmCalendar";

interface Props {
  events: CalendarEvent[];
  currentMonth: Date;
}

function fmt(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
}

function fmtPct(value: number) {
  return `${value >= 0 ? "+" : ""}${value.toFixed(1)}%`;
}

interface RecapRow {
  label: string;
  amount: number;
  currency: string;
  icon: React.ReactNode;
  color: string;
}

export default function PropFirmMonthRecap({ events, currentMonth }: Props) {
  const t = useTranslation();
  const isDark = useIsDark();

  const monthEvents = useMemo(
    () => events.filter(e => isSameMonth(new Date(e.date), currentMonth)),
    [events, currentMonth]
  );

  const stats = useMemo(() => {
    let challenges = 0;
    let activations = 0;
    let subscriptions = 0;
    let payouts = 0;

    for (const e of monthEvents) {
      if (e.type === "payout") {
        payouts += e.amount;
      } else if (e.type === "recurring") {
        subscriptions += e.amount;
      } else if (e.type === "expense") {
        // expenses from account creation tagged by label convention — we use type alone
        // challenges vs activations distinction is lost at this level, lump into expenses
        challenges += e.amount;
      }
    }

    const totalExpenses = challenges + activations + subscriptions;
    const roi = totalExpenses > 0 ? ((payouts - totalExpenses) / totalExpenses) * 100 : null;

    return { challenges, activations, subscriptions, payouts, totalExpenses, roi, currency: CURRENCY };
  }, [monthEvents]);

  const rows: RecapRow[] = [
    {
      label: t.propFirmManager.monthRecapChallenges,
      amount: stats.challenges,
      currency: CURRENCY,
      icon: <CreditCard className="h-3.5 w-3.5" />,
      color: "text-pnlNegative",
    },
    {
      label: t.propFirmManager.monthRecapActivations,
      amount: stats.activations,
      currency: CURRENCY,
      icon: <DollarSign className="h-3.5 w-3.5" />,
      color: "text-pnlNegative",
    },
    {
      label: t.propFirmManager.monthRecapSubscriptions,
      amount: stats.subscriptions,
      currency: CURRENCY,
      icon: <Repeat className="h-3.5 w-3.5" />,
      color: "text-orange-400",
    },
    {
      label: t.propFirmManager.monthRecapPayouts,
      amount: stats.payouts,
      currency: CURRENCY,
      icon: <Wallet className="h-3.5 w-3.5" />,
      color: "text-pnlPositive",
    },
  ];

  const isEmpty = monthEvents.length === 0;

  return (
    <Card className="h-full">
      <CardSectionHeader label={t.propFirmManager.monthRecapTitle} isDark={isDark} />
      <CardContent className="px-5 pb-5 pt-1">
        {isEmpty ? (
          <div className="flex items-center justify-center h-40 text-sm text-muted-foreground">
            {t.propFirmManager.monthRecapNoData}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {rows.map(row => (
              <div key={row.label} className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`shrink-0 ${row.color}`}>{row.icon}</span>
                  <span className="text-sm text-muted-foreground truncate">{row.label}</span>
                </div>
                <span className={`text-sm font-semibold font-mono shrink-0 ${row.amount > 0 ? row.color : "text-muted-foreground/40"}`}>
                  {row.amount > 0 ? (row.label === t.propFirmManager.monthRecapPayouts ? "+" : "−") + fmt(row.amount, CURRENCY) : "—"}
                </span>
              </div>
            ))}

            <div className={`mt-2 pt-3 border-t border-border flex items-center justify-between gap-2`}>
              <span className="text-sm font-semibold text-muted-foreground">{t.propFirmManager.monthRecapTotalExpenses}</span>
              <span className={`text-sm font-bold font-mono ${stats.totalExpenses > 0 ? "text-pnlNegative" : "text-muted-foreground/40"}`}>
                {stats.totalExpenses > 0 ? `−${fmt(stats.totalExpenses, CURRENCY)}` : "—"}
              </span>
            </div>

            <div className={`pt-2 border-t border-border`}>
              <div className="rounded-xl p-4 flex flex-col gap-1 ${isDark ? 'bg-muted/20' : 'bg-muted/30'}">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5">
                    {stats.roi !== null && stats.roi >= 0
                      ? <TrendingUp className="h-4 w-4 text-pnlPositive" />
                      : <TrendingDown className="h-4 w-4 text-pnlNegative" />
                    }
                    <span className="text-sm font-semibold">{t.propFirmManager.monthRecapRoi}</span>
                  </div>
                  <span className={`text-xl font-bold font-mono ${
                    stats.roi === null ? "text-muted-foreground/40"
                    : stats.roi >= 0 ? "text-pnlPositive"
                    : "text-pnlNegative"
                  }`}>
                    {stats.roi !== null ? fmtPct(stats.roi) : "—"}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1">
                  <span className="text-xs text-muted-foreground">{t.propFirmManager.monthRecapPayouts}</span>
                  <span className="text-xs font-mono font-semibold text-pnlPositive">
                    {stats.payouts > 0 ? `+${fmt(stats.payouts, CURRENCY)}` : "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
